import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { PropsWithChildren, useEffect, useState } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';

// Context
import { MercuryPagesProvider } from './context/MercuryPagesContext';
import { MercuryPermissionsProvider } from './context/MercuryPermissions';
import { ToastContextProvider } from './context/ToastContext';
import { ToastContainer } from './components/content/toast/ToastContainer';
import { ConfirmationProvider } from './components/content/confirmationdialog/ConfirmationContext';
import { SessionContextProvider } from './context/SessionContext';
import { DrawerProvider } from './context/DrawerContext';
import { AppErrorBoundary } from './components/errors/AppErrorBoundary';
import RateLimit from './client/features/ratelimit/RateLimit';

import { queryClient as qc } from './lib/react-query/client';
import { PermanentMessageProvider } from './context/PermanentMessageContext';
import { PermanentMessage } from './components/content/permanentmessage/PermanentMessage';
import { initPostHog } from '@/lib/posthog/posthog';
import { POSTHOG_EVENTS } from '@/lib/posthog/events';
import { trackEvent } from '@/lib/posthog/helpers';
import { FeatureFlagsProvider } from '@/context/FeatureFlagsContext';
import { DesignSystemProvider } from '@/theme/DesignSystemProvider';
import { colorSchemeManager } from './theme/colorScheme';

function resolveColorScheme(): 'dark' | 'light' {
  const stored = colorSchemeManager.get('auto');
  if (stored === 'dark' || stored === 'light') return stored;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

const App = ({ children }: PropsWithChildren) => {
  const [queryClient] = useState(qc());
  initPostHog();

  useEffect(() => {
    trackEvent(POSTHOG_EVENTS.UI_THEME_CURRENT, { theme: resolveColorScheme() });
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <FeatureFlagsProvider>
        <DesignSystemProvider>
          <MercuryPagesProvider>
            <MercuryPermissionsProvider>
              <ConfirmationProvider>
                <ToastContextProvider>
                  <DrawerProvider>
                    <PermanentMessageProvider>
                      <SessionContextProvider>
                        <AppErrorBoundary>
                          {children}
                          <ToastContainer />
                          <PermanentMessage />
                          <RateLimit />
                          {process.env.NODE_ENV !== 'production' && (
                            <ReactQueryDevtools initialIsOpen={false} />
                          )}
                        </AppErrorBoundary>
                      </SessionContextProvider>
                    </PermanentMessageProvider>
                  </DrawerProvider>
                </ToastContextProvider>
              </ConfirmationProvider>
            </MercuryPermissionsProvider>
          </MercuryPagesProvider>
        </DesignSystemProvider>
      </FeatureFlagsProvider>
    </QueryClientProvider>
  );
};

export default App;
